import { useEffect, useState } from 'react'
import { apiService, type LiveReadings } from '@/service/api'

export interface UseLiveReadingsQuery {
  /** Tag ids, at most 10. Omitted or empty: the server picks up to 5. */
  tags?: string[]
  /** `15m` / `1h` / `1d` or an ISO 8601 duration. Server default: 1 hour. */
  window?: string
}

export interface UseLiveReadingsOptions {
  /** Poll period. The server caches an answer 30 s, so going under that is wasted. */
  intervalMs?: number
}

export interface UseLiveReadingsResult {
  /** The newest good answer for this document; kept when a later poll fails. */
  data: LiveReadings | null
  /** The last poll's failure; cleared by the next success. */
  error: Error | null
  /** True until the first answer or failure for this document. */
  loading: boolean
  /** When `data` arrived (ms since epoch), for an "updated N s ago" line. */
  updatedAt: number | null
}

interface Settled {
  documentId: string
  data: LiveReadings | null
  error: Error | null
  updatedAt: number | null
}

/**
 * Polls a time-series document's live readings: once on mount, then every `intervalMs`, and not
 * at all while the tab is hidden (becoming visible refetches at once). A failed poll keeps the
 * last good `data`; a response that a newer request or a change of document has overtaken is
 * dropped. Changing `tags`/`window` refetches immediately and keeps showing the old data until
 * the new answer lands.
 */
export function useLiveReadings(
  documentId: string | undefined,
  query: UseLiveReadingsQuery = {},
  { intervalMs = 60_000 }: UseLiveReadingsOptions = {}
): UseLiveReadingsResult {
  const [settled, setSettled] = useState<Settled | null>(null)
  const { window: windowSpec } = query
  // A fresh `tags` array each render must not restart the poll; its contents do.
  const tagsKey = JSON.stringify(query.tags ?? [])

  useEffect(() => {
    if (!documentId) return
    const tags: string[] = JSON.parse(tagsKey)
    let disposed = false
    let latest = 0
    let timer: ReturnType<typeof setInterval> | undefined

    const poll = async () => {
      const mine = ++latest
      try {
        const data = await apiService.getLiveReadings(documentId, { tags, window: windowSpec })
        if (disposed || mine !== latest) return
        setSettled({ documentId, data, error: null, updatedAt: Date.now() })
      } catch (e) {
        if (disposed || mine !== latest) return
        const error = e instanceof Error ? e : new Error(String(e))
        setSettled((prev) => ({
          documentId,
          data: prev?.documentId === documentId ? prev.data : null,
          error,
          updatedAt: prev?.documentId === documentId ? prev.updatedAt : null,
        }))
      }
    }
    const stop = () => {
      if (timer !== undefined) clearInterval(timer)
      timer = undefined
    }
    const start = () => {
      stop()
      void poll()
      timer = setInterval(() => void poll(), intervalMs)
    }
    const onVisibility = () => {
      if (document.hidden) stop()
      else start()
    }

    if (!document.hidden) start()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      disposed = true
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [documentId, tagsKey, windowSpec, intervalMs])

  const mine = settled && settled.documentId === documentId ? settled : null
  return {
    data: mine?.data ?? null,
    error: mine?.error ?? null,
    loading: !!documentId && !mine,
    updatedAt: mine?.updatedAt ?? null,
  }
}
